import {consumerUrls as urls} from "../consumerUrls";
import React, {PropsWithChildren} from "react";
import {generatePath, Navigate, useLocation} from "react-router-dom";
import {useGetAccount} from "~/api/requests";
import {KEY_SKIP_ACCOUNTS} from "~/constants/no-login-keys";
import {useAuth} from "@bounce/shared/contexts";
import {useLoginDestinationUrl, useConsumerUser} from "~/contexts";
import {isPreLoginRole} from "~/helpers/roles";
import {useAccountId, useUiSettings} from "~/hooks";
import {isNotCollectible} from "~/helpers/account";

const AuthGuard = ({children}: PropsWithChildren) => {
  const {loggedIn, userRole, userId: authUserId} = useAuth();
  const {uiSettings} = useUiSettings();
  const {isNoLogin, preLoginContext} = useConsumerUser();
  const accountId = useAccountId();
  const {pathname, search} = useLocation();
  const {getDestinationUrl, clearDestinationUrl} = useLoginDestinationUrl();

  const queryParams = new URLSearchParams(search);

  const userId = queryParams.get("id") || authUserId || uiSettings?.userId || undefined;
  const {data: account} = useGetAccount({accountId}, {enabled: !!((loggedIn || isNoLogin) && accountId)});

  const shouldRedirect = isNotCollectible(account);

  const destinationUrl = getDestinationUrl();
  if (loggedIn && destinationUrl) {
    const skipAccounts = sessionStorage.getItem(KEY_SKIP_ACCOUNTS);
    clearDestinationUrl();
    if (skipAccounts === "true" && destinationUrl?.includes("/accounts")) {
      sessionStorage.removeItem(KEY_SKIP_ACCOUNTS);
      if (preLoginContext?.account_id) {
        return <Navigate to={generatePath(urls.accountMain.url, {accountId: preLoginContext.account_id})} replace />;
      }
    }
    if (destinationUrl) {
      return <Navigate to={destinationUrl} replace />;
    }
  }

  if (!loggedIn) {
    if (isNoLogin && shouldRedirect) {
      return <Navigate to={urls.accounts.url + search} state={{origin: pathname + search}} />;
    }
    if (isPreLoginRole(userRole) && userId) {
      return <>{children}</>;
    }
    if (search && userId) {
      return <Navigate to={urls.login.url + search} state={{origin: pathname + search}} />;
    } else if (userId) {
      return <Navigate to={urls.login.url + `?id=${userId}`} state={{origin: pathname}} />;
    } else {
      return <Navigate to={urls.login.url} state={{origin: pathname}} />;
    }
  }

  if (shouldRedirect) {
    return <Navigate to={urls.accounts.url + search} state={{origin: pathname + search}} />;
  }
  return <>{children}</>;
};

export default AuthGuard;
